// What a person does to an app, on the list's row menu and on the app's page
// alike (02-DESIGN/01-identity/04-apps.md § revocation and rotation, §
// re-attestation and suspension; 01-the-console.md § conventions: a row's
// actions): *Restore* a suspended app, *New key*, which replaces the key at
// once and hands the new one over in the page's place, and *Disconnect*,
// which makes the app's next request fail. Each is asked first. A key — a
// new app's or a *New key*'s — is held in memory only while its step is the
// form the page shows: leaving the step, by *Done* or by Back, drops it for
// good, so Forward finds nothing to show again (keyStep).

import { useState } from "react";

import {
  AppKind,
  Subjects,
  type App,
  type AppCredentialReply,
  type AppReply,
  type AppRequest,
} from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import type { MenuEntry } from "../../ui/table.tsx";
import type { Surface } from "../surface.tsx";
import { isSuspended, restoreConsequence } from "./state.ts";

/** Held is a key the page shows once: a new app's (`fresh`) or a *New key*'s. */
export interface Held {
  reply: AppCredentialReply;
  fresh: boolean;
}

/** KEY_STEP is the form that shows a key: the last step of *Connect an app* and of *New key*. */
export const KEY_STEP = "key";

/**
 * keyStep is what a change of the page's form does to the key it holds:
 * leaving the key step drops the key, and a new app's leaving marks its row
 * in the list. Any other change keeps what is held.
 */
export function keyStep(
  from: string | undefined,
  to: string | undefined,
  held: Held | undefined,
): { held: Held | undefined; mark: string | undefined } {
  if (from === KEY_STEP && to !== KEY_STEP && held !== undefined) {
    return { held: undefined, mark: held.fresh ? held.reply.app.id : undefined };
  }
  return { held, mark: undefined };
}

/** shownKey is the key the page draws: only while the key step is the form open. */
export function shownKey(open: string | undefined, held: Held | undefined): Held | undefined {
  return open === KEY_STEP ? held : undefined;
}

/** AppActs is the acts on apps one page offers, and the key one just made, shown once. */
export interface AppActs {
  err: unknown;
  /** The key the page shows in its place, while the key step is open. */
  key: Held | undefined;
  /** The new app whose row the list marks, once its key step closed. */
  marked: string | undefined;
  /** connected holds a new app's key, as *Connect an app* hands it over. */
  connected: (reply: AppCredentialReply) => void;
  menuFor: (a: App) => MenuEntry[];
  /** byName is who connected an app, as the page says it: *you*, or their name. */
  byName: (a: App) => string;
  /** actsAs is what an app acts as: *you*, the workspace's name, or its person. */
  actsAs: (a: App) => string;
  restores: boolean;
}

/**
 * useAppActs holds the acts on apps for a page. `open` is the form the page
 * shows; `onKey` opens the key step once a key exists; `reload` after any act.
 */
export function useAppActs(
  s: Surface,
  reload: () => void,
  open: string | undefined,
  onKey: () => void,
): AppActs {
  const [err, setErr] = useState<unknown>(undefined);
  const [held, setHeld] = useState<Held | undefined>(undefined);
  const [marked, setMarked] = useState<string | undefined>(undefined);
  // The form changed since the last render: apply what that does to the key.
  const [was, setWas] = useState(open);
  if (was !== open) {
    const next = keyStep(was, open, held);
    setWas(open);
    if (next.held !== held) {
      setHeld(next.held);
    }
    if (next.mark !== undefined) {
      setMarked(next.mark);
    }
  }
  const restores = s.offered("apps:manage:any");
  const newKey = (app: App) => {
    setErr(undefined);
    s.bus.call<AppCredentialReply>(SERVICE, Subjects.appsRotate(), { id: app.id } satisfies AppRequest).then(
      (reply) => {
        setHeld({ reply, fresh: false });
        onKey();
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const restore = (app: App) => {
    setErr(undefined);
    s.bus.call<AppReply>(SERVICE, Subjects.appsRestore(), { id: app.id } satisfies AppRequest).then(
      () => {
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const disconnect = (app: App) => {
    setErr(undefined);
    s.bus.call<AppReply>(SERVICE, Subjects.appsRevoke(), { id: app.id } satisfies AppRequest).then(
      () => {
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const byName = (a: App) => (a.registrant.subject === s.me ? "you" : (a.registrant.display ?? "its person"));
  const actsAs = (a: App): string =>
    a.kind === AppKind.tenant
      ? s.name
      : a.registrant.subject === s.me
        ? "you"
        : (a.registrant.display ?? "a person");
  const restoreEntry = (a: App): MenuEntry[] =>
    isSuspended(a) && restores
      ? [
          {
            label: "Restore",
            confirm: {
              title: `Restore ${a.name}?`,
              consequence: restoreConsequence(byName(a), s.name),
            },
            onSelect: () => {
              restore(a);
            },
          },
        ]
      : [];
  const menuFor = (a: App): MenuEntry[] =>
    a.active && s.manages(a.registrant.subject)
      ? [
          ...restoreEntry(a),
          {
            label: "New key",
            confirm: {
              title: `A new key for ${a.name}?`,
              consequence: "The current key stops working at once.",
            },
            onSelect: () => {
              newKey(a);
            },
          },
          {
            label: "Disconnect",
            danger: true,
            confirm: {
              title: `Disconnect ${a.name}?`,
              consequence: "Its next request fails. It stays listed as disconnected.",
            },
            onSelect: () => {
              disconnect(a);
            },
          },
        ]
      : [];
  return {
    err,
    key: shownKey(open, held),
    marked,
    connected: (reply) => {
      setHeld({ reply, fresh: true });
      onKey();
    },
    menuFor,
    byName,
    actsAs,
    restores,
  };
}
